/**
 * v3.19-1430 · Native Internal Messaging — the primary Hub messaging
 * client.  Consumes the built-in `/api/messages/*` REST surface and
 * subscribes to the existing WebSocket (`/ws/{token}`) for immediate
 * push of new messages.
 *
 * FEATURES SHIPPED THIS SPRINT:
 *   • Conversations list with unread badge + last message preview
 *   • Real-time delivery (no polling) via WebSocket `type=message`
 *   • Auto-mark-as-read when a conversation is opened
 *   • Conversation search (client-side) + user search (server-side)
 *   • Send composer — Enter=send, Shift+Enter=newline
 *   • Message delete (own messages only)
 *   • CRM linking badge (renders `client_id` link when present)
 *   • Web-push fan-out is triggered server-side (see server.py:31352)
 *
 * DEFERRED (documented — require backend schema changes):
 *   • Group conversations (backend model is strictly 1:1 today)
 *   • Typing indicators (WebSocket carries `type=message` only)
 *   • Message edit (backend supports delete only)
 *   • File attachments (schema has no attachment field)
 *   • Emoji picker (typing native emoji already works)
 *   • Read-receipt UI (backend has `read` bool; UI shown as timestamp)
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  MessageSquare, Send, Search as SearchIcon, X, Loader2, Plus,
  Trash2, User as UserIcon, RefreshCw, Zap, Link2,
} from 'lucide-react';
import api from '../lib/api';
import wsService from '../services/websocket';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60)    return `${s}s`;
  if (s < 3600)  return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
};

const timeOfDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const initialsFrom = (name, email) => {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.replace(/[()]/g, ' ').split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AVATAR_TONES = [
  'bg-emerald-100 text-emerald-800',
  'bg-teal-100 text-teal-800',
  'bg-slate-200 text-slate-800',
  'bg-amber-100 text-amber-800',
  'bg-sky-100 text-sky-800',
  'bg-violet-100 text-violet-800',
  'bg-rose-100 text-rose-800',
];
const toneFor = (seed) => AVATAR_TONES[(seed || '?').charCodeAt(0) % AVATAR_TONES.length];

function Avatar({ initials, size = 'md' }) {
  const sz = size === 'sm' ? 'h-7 w-7 text-[10px]'
           : size === 'lg' ? 'h-10 w-10 text-sm'
           : 'h-8 w-8 text-xs';
  return (
    <div className={`${sz} rounded-full flex items-center justify-center font-semibold shrink-0 ${toneFor(initials)}`}>
      {initials}
    </div>
  );
}


export default function NativeMessagingTab() {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id || currentUser?.user_id;

  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [activePartner, setActivePartner] = useState(null); // { user_id, user_name, ... }
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const scrollRef = useRef(null);

  // -------------------------------------------------------- data loading
  const loadConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const r = await api.get('/messages/conversations');
      const list = Array.isArray(r.data) ? r.data : [];
      list.sort((a, b) => (b.last_message_time || '').localeCompare(a.last_message_time || ''));
      setConversations(list);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load conversations');
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (partnerId) => {
    if (!partnerId) { setMessages([]); return; }
    setMessagesLoading(true);
    try {
      const r = await api.get(`/messages/${partnerId}`);
      setMessages(Array.isArray(r.data) ? r.data : []);
      // The backend auto-marks messages read on GET, so refresh the
      // conversation list so the unread count clears immediately.
      loadConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  }, [loadConversations]);

  useEffect(() => { loadConversations(); }, [loadConversations]);
  useEffect(() => { if (activePartner) loadMessages(activePartner.user_id); }, [activePartner, loadMessages]);

  // --------------------------------------- WebSocket: real-time delivery
  useEffect(() => {
    if (!currentUserId) return;
    const unsub = wsService.subscribe('message', (data) => {
      // A new message just arrived for me from `data.sender_id`.
      // Two updates:
      //   1. If the thread with that sender is open, append the
      //      message immediately.
      //   2. Refresh the conversations list so the unread badge /
      //      last-message preview update.
      if (activePartner && data.sender_id === activePartner.user_id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.id)) return prev;
          return [...prev, {
            id:             data.id,
            sender_id:      data.sender_id,
            sender_name:    data.sender_name,
            recipient_id:   data.recipient_id || currentUserId,
            recipient_name: currentUser?.full_name || '',
            content:        data.content,
            message_type:   data.message_type || 'general',
            client_id:      data.client_id,
            read:           false,
            created_at:     data.created_at,
          }];
        });
        // Auto-mark read in-place since the thread is being viewed.
        api.put(`/messages/${data.id}/read`).catch(() => {});
      }
      loadConversations();
    });
    return () => { try { unsub && unsub(); } catch (err) { /* noop */ } };
  }, [activePartner, currentUserId, currentUser, loadConversations]);

  // Auto-scroll on load or on new content.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, activePartner?.user_id]);

  // ---------------------------------------------------------------- send
  const send = async () => {
    const text = draft.trim();
    if (!text || sending || !activePartner) return;
    setSending(true);
    // Optimistic append so the sender sees their own message instantly.
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      sender_id: currentUserId,
      sender_name: currentUser?.full_name || 'You',
      recipient_id: activePartner.user_id,
      recipient_name: activePartner.user_name,
      content: text,
      message_type: 'general',
      client_id: null,
      read: false,
      created_at: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages((prev) => [...prev, optimistic]);
    setDraft('');
    try {
      const r = await api.post('/messages', {
        recipient_id: activePartner.user_id,
        content:      text,
        message_type: 'general',
      });
      // Replace the temp with the confirmed row (server returned the id).
      setMessages((prev) => prev.map((m) =>
        m.id === tempId
          ? { ...optimistic, id: r.data.id, _optimistic: false }
          : m,
      ));
      loadConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send message');
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };
  const onComposerKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const deleteMessage = async (m) => {
    if (m._optimistic) return;
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/messages/${m.id}`);
      setMessages((prev) => prev.filter((x) => x.id !== m.id));
      loadConversations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  // --------------------------------------------------------- filtering
  const filteredConversations = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      (c.user_name || '').toLowerCase().includes(q)
      || (c.user_company || '').toLowerCase().includes(q)
      || (c.last_message || '').toLowerCase().includes(q));
  }, [conversations, filter]);

  const totalUnread = conversations.reduce((n, c) => n + (c.unread_count || 0), 0);

  // -------------------------------------------------------------- render
  return (
    <div className="space-y-4" data-testid="native-messaging-tab">
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-emerald-700" />
            Internal Messaging
            <Badge variant="outline" className="border-emerald-200 text-emerald-800 bg-emerald-50 text-[10px] py-0 h-5">
              <Zap className="h-3 w-3 mr-1" /> Real-time
            </Badge>
            {totalUnread > 0 && (
              <Badge className="bg-red-600 text-white text-[10px] py-0 h-5" data-testid="total-unread-badge">
                {totalUnread} unread
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => { loadConversations(); if (activePartner) loadMessages(activePartner.user_id); }} title="Refresh" data-testid="native-msg-refresh">
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-3 h-[600px]">
            {/* --------------------------------------------- CONVERSATIONS */}
            <div className="border border-slate-200 rounded-md overflow-hidden flex flex-col bg-white">
              <div className="border-b bg-slate-50 p-2 space-y-2">
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                  <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Search conversations"
                    className="pl-8 h-8 text-sm"
                    data-testid="native-msg-filter"
                  />
                  {filter && (
                    <button
                      type="button"
                      onClick={() => setFilter('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      aria-label="Clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <Button
                  size="sm"
                  className="w-full h-8 bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => setNewChatOpen(true)}
                  data-testid="native-msg-new-chat"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> New Message
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {conversationsLoading && (
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </div>
                )}
                {!conversationsLoading && filteredConversations.length === 0 && conversations.length === 0 && (
                  <div className="p-4 text-xs text-slate-500 italic">
                    No conversations yet. Click <span className="font-semibold">New Message</span> to start one.
                  </div>
                )}
                {!conversationsLoading && filteredConversations.length === 0 && conversations.length > 0 && (
                  <div className="p-3 text-xs text-slate-500 italic">No matches for &ldquo;{filter}&rdquo;.</div>
                )}
                {filteredConversations.map((c) => {
                  const active = activePartner?.user_id === c.user_id;
                  return (
                    <button
                      type="button"
                      key={c.user_id}
                      onClick={() => setActivePartner(c)}
                      className={`w-full text-left px-3 py-2.5 border-b border-slate-100 flex items-start gap-2.5 ${active ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                      data-testid={`native-conv-${c.user_id}`}
                    >
                      <Avatar initials={initialsFrom(c.user_name)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm ${active ? 'text-emerald-900 font-semibold' : 'text-slate-900 font-medium'}`}>
                            {c.user_name}
                          </span>
                          <span className="text-[10px] text-slate-400 shrink-0">{relTime(c.last_message_time)}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <span className="text-[11px] text-slate-500 truncate flex-1">{c.last_message || 'No messages yet'}</span>
                          {c.unread_count > 0 && (
                            <span className="text-[10px] bg-red-600 text-white rounded-full px-1.5 h-4 min-w-[16px] flex items-center justify-center font-semibold" data-testid={`native-unread-${c.user_id}`}>
                              {c.unread_count}
                            </span>
                          )}
                        </div>
                        {c.user_company && (
                          <div className="text-[10px] text-slate-400 truncate mt-0.5">{c.user_company}</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* -------------------------------------------------- THREAD */}
            <div className="border border-slate-200 rounded-md overflow-hidden flex flex-col bg-white" data-testid="native-thread-panel">
              <div className="text-sm font-semibold text-slate-900 px-3 py-2.5 border-b bg-slate-50 flex items-center gap-2.5">
                {activePartner ? (
                  <>
                    <Avatar initials={initialsFrom(activePartner.user_name)} size="sm" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{activePartner.user_name}</div>
                      {activePartner.user_company && (
                        <div className="text-[11px] text-slate-500 font-normal truncate">{activePartner.user_company}</div>
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-slate-500 font-normal">Select a conversation</span>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-1.5 bg-slate-50/60" data-testid="native-messages-scroll">
                {messagesLoading && (
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                  </div>
                )}
                {!messagesLoading && activePartner && messages.length === 0 && (
                  <div className="text-xs text-slate-500 italic text-center py-8">No messages yet.  Say hello below.</div>
                )}
                {messages.map((m, idx) => {
                  const isMine = m.sender_id === currentUserId;
                  const prev = idx > 0 ? messages[idx - 1] : null;
                  const showSender = !prev || prev.sender_id !== m.sender_id;
                  return (
                    <div key={m.id} className={`flex flex-col group ${isMine ? 'items-end' : 'items-start'}`} data-testid={`native-msg-${m.id}`}>
                      {showSender && !isMine && (
                        <div className="text-[11px] text-slate-500 mb-0.5 ml-1">{m.sender_name || 'Unknown'}</div>
                      )}
                      <div className="flex items-center gap-1.5">
                        {isMine && !m._optimistic && (
                          <button
                            type="button"
                            onClick={() => deleteMessage(m)}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-600"
                            title="Delete"
                            data-testid={`native-msg-del-${m.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                        <div className={`max-w-[500px] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words shadow-sm ${
                          isMine
                            ? `bg-emerald-600 text-white rounded-br-md ${m._optimistic ? 'opacity-70' : ''}`
                            : 'bg-white text-slate-900 border border-slate-200 rounded-bl-md'
                        }`}>
                          {m.client_name && (
                            <div className={`text-[10px] mb-1 flex items-center gap-1 ${isMine ? 'text-emerald-100' : 'text-slate-500'}`}>
                              <Link2 className="h-3 w-3" /> Linked to {m.client_name}
                            </div>
                          )}
                          {m.content}
                        </div>
                      </div>
                      <div className={`text-[10px] mt-0.5 ${isMine ? 'text-slate-400 mr-1' : 'text-slate-400 ml-1'}`}>
                        {timeOfDay(m.created_at)}
                        {isMine && m.read && <span className="ml-1 text-emerald-600 font-semibold">✓ Read</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {activePartner && (
                <div className="border-t border-slate-200 p-2.5 bg-white flex items-end gap-2" data-testid="native-composer">
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onComposerKey}
                    rows={2}
                    placeholder={`Message ${activePartner.user_name}…   (Enter to send · Shift+Enter for new line)`}
                    className="min-h-[52px] text-sm resize-none"
                    data-testid="native-composer-input"
                  />
                  <Button
                    size="sm"
                    onClick={send}
                    disabled={!draft.trim() || sending}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white h-[52px] px-4"
                    data-testid="native-composer-send"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Send</>}
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            Native Hub messaging · WebSocket real-time · Web-push to mobile/PWA · read receipts on view · CRM-linkable via <UserIcon className="inline h-3 w-3" /> Client Detail.
          </div>
        </CardContent>
      </Card>

      <NewMessageDialog
        open={newChatOpen}
        onOpenChange={setNewChatOpen}
        currentUserId={currentUserId}
        onSelectUser={(u) => {
          setActivePartner({
            user_id:      u.id,
            user_name:    u.full_name || u.email,
            user_company: u.company_name || null,
            last_message: '',
            last_message_time: null,
            unread_count: 0,
          });
          setNewChatOpen(false);
        }}
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// New-message dialog — searches Hub users via `/api/users`.
// ---------------------------------------------------------------------------
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';

function NewMessageDialog({ open, onOpenChange, currentUserId, onSelectUser }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open) { setQuery(''); setResults([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get('/users', { params: { limit: 200 } });
        if (!cancelled) setResults(Array.isArray(r.data) ? r.data : []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = results.filter((u) => u.id !== currentUserId && u.is_active !== false);
    if (q) {
      out = out.filter((u) => {
        const name  = (u.full_name || '').toLowerCase();
        const email = (u.email || '').toLowerCase();
        return name.includes(q) || email.includes(q);
      });
    }
    return out.slice(0, 40);
  }, [results, query, currentUserId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="native-new-msg-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-emerald-700" /> New Message
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Hub users by name or email"
              className="pl-8"
              data-testid="native-new-msg-search"
            />
          </div>
          <div className="max-h-[360px] overflow-y-auto border border-slate-100 rounded-md">
            {loading && (
              <div className="p-4 text-xs text-slate-500 flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading users…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="p-4 text-xs text-slate-500 italic">No users matched.</div>
            )}
            {filtered.map((u) => (
              <button
                type="button"
                key={u.id}
                onClick={() => onSelectUser(u)}
                className="w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center gap-3 hover:bg-emerald-50"
                data-testid={`native-new-msg-user-${u.id}`}
              >
                <Avatar initials={initialsFrom(u.full_name, u.email)} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{u.full_name || u.email}</div>
                  <div className="text-[11px] text-slate-500 truncate">{u.email}</div>
                </div>
                {u.role && (
                  <Badge variant="outline" className="text-[10px] py-0 h-4 border-slate-200 text-slate-700">
                    {u.role.replace(/_/g, ' ')}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
